import {
    AlertTriangle,
    ArrowRight,
    CalendarClock,
    CheckCircle2,
    LoaderCircle,
    RefreshCw,
    Search,
    ShieldCheck,
    User,
    Users,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './FnFHomeScreen.module.css'

const STATUS_ORDER = {
    BLOCKED: 0,
    WARNING: 1,
    READY: 2,
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const normalizeStaffList = (payload) => {
    const candidates = [
        payload?.staff,
        payload?.staffReadiness,
        payload?.staffList,
        payload?.response,
    ]

    const staffArray = candidates.find(Array.isArray) || []

    return staffArray.map((item, index) => ({
        staffIdentifier: item?.staffIdentifier || item?.employeeIdentifier || item?.id || `staff-${index}`,
        staffName: item?.staffName || item?.name || item?.fullName || 'Unnamed Staff',
        staffCode: item?.staffCode || item?.employeeCode || '',
        readinessStatus: String(item?.readinessStatus || 'BLOCKED').toUpperCase(),
        issues: Array.isArray(item?.issues) ? item.issues.filter(Boolean) : [],
    }))
}

const getStatusMeta = (status) => {
    if (status === 'READY') return { label: 'Ready', className: styles.badgeReady }
    if (status === 'WARNING') return { label: 'Warning', className: styles.badgeWarning }
    return { label: 'Blocked', className: styles.badgeBlocked }
}

const FnFHomeScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [staff, setStaff] = useState([])
    const [searchText, setSearchText] = useState('')

    const fetchStaffReadiness = useCallback(async ({ silent = false } = {}) => {
        if (!storeIdentifier) {
            setErrorMessage('Please select a store first to view staff list.')
            setStaff([])
            setIsLoading(false)
            return
        }

        if (!silent) {
            setIsLoading(true)
        } else {
            setIsRefreshing(true)
        }
        setErrorMessage('')

        try {
            const response = await fetch(BASE_URL_API + 'getPayrollStaffReadiness', {
                method: 'POST',
                cache: 'no-store',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                }),
            })

            if (!response.ok) {
                throw new Error('Server error')
            }

            const result = await response.json()
            if (result?.status === 'success') {
                const payload = result?.response || {}
                setStaff(normalizeStaffList(payload))
                return
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            setErrorMessage(result?.message || 'Unable to fetch staff list.')
            setStaff([])
        } catch {
            setErrorMessage('Unable to connect to server. Please try again.')
            setStaff([])
        } finally {
            setIsLoading(false)
            setIsRefreshing(false)
        }
    }, [authCtx, storeIdentifier])

    useEffect(() => {
        fetchStaffReadiness({ silent: false })
    }, [fetchStaffReadiness])

    const filteredStaff = useMemo(() => {
        const query = searchText.trim().toLowerCase()
        const sorted = [...staff].sort((a, b) => {
            const orderA = STATUS_ORDER[a?.readinessStatus] ?? 99
            const orderB = STATUS_ORDER[b?.readinessStatus] ?? 99
            if (orderA !== orderB) return orderA - orderB
            return String(a?.staffName || '').localeCompare(String(b?.staffName || ''))
        })

        if (!query) return sorted

        return sorted.filter((item) =>
            [item?.staffName, item?.staffCode, item?.staffIdentifier]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query))
        )
    }, [searchText, staff])

    return (
        <div className={styles.page}>
            <section className={styles.heroCard}>
                <div className={styles.heroTopRow}>
                    <span className={styles.heroPill}>
                        <CalendarClock size={14} /> Full &amp; Final Settlements
                    </span>
                    {isRefreshing ? (
                        <span className={styles.syncHint}>
                            <RefreshCw size={12} className={styles.spinning} /> Syncing latest list...
                        </span>
                    ) : null}
                </div>

                <h1>Start FnF for the right staff in one click.</h1>
                <p>
                    Pick a staff member below to generate their Full &amp; Final settlement and continue to
                    detailed calculation.
                </p>

                <div className={styles.quickStats}>
                    <span><Users size={13} /> Total staff: {staff.length}</span>
                    <span><ShieldCheck size={13} /> FnF flow supports draft → accepted → paid</span>
                </div>
            </section>

            {!storeIdentifier ? (
                <section className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Select an active store first.</strong>
                        <p>FnF is store-specific. Please select a store to continue.</p>
                    </div>
                </section>
            ) : null}

            {errorMessage ? (
                <section className={`${styles.alertCard} ${styles.errorCard}`}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Could not load staff list.</strong>
                        <p>{errorMessage}</p>
                        <button
                            type="button"
                            className={styles.retryBtn}
                            onClick={() => fetchStaffReadiness({ silent: false })}
                        >
                            <RefreshCw size={13} /> Retry
                        </button>
                    </div>
                </section>
            ) : null}

            {!errorMessage && (
                <section className={styles.listSection}>
                    <div className={styles.listHeader}>
                        <h2>Select Staff for FnF</h2>
                        <button
                            type="button"
                            className={styles.refreshBtn}
                            onClick={() => fetchStaffReadiness({ silent: true })}
                            disabled={isRefreshing || isLoading}
                        >
                            <RefreshCw size={13} className={isRefreshing ? styles.spinning : ''} /> Refresh
                        </button>
                    </div>

                    <div className={styles.searchBar}>
                        <Search size={15} />
                        <input
                            type="text"
                            placeholder="Search by name, code or staff ID"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                        />
                    </div>

                    {isLoading ? (
                        <div className={styles.loaderBox}>
                            <LoaderCircle size={18} className={styles.spinning} />
                            <p>Loading staff list...</p>
                        </div>
                    ) : null}

                    {!isLoading && filteredStaff.length === 0 ? (
                        <div className={styles.emptyState}>
                            <Users size={18} />
                            <p>{staff.length === 0 ? 'No active staff found.' : 'No staff matched your search.'}</p>
                        </div>
                    ) : null}

                    {!isLoading && filteredStaff.length > 0 ? (
                        <div className={styles.cardsGrid}>
                            {filteredStaff.map((item) => {
                                const statusMeta = getStatusMeta(item?.readinessStatus)
                                return (
                                    <button
                                        key={item?.staffIdentifier}
                                        type="button"
                                        className={styles.staffCard}
                                        onClick={() =>
                                            navigate(`/payroll/fnf/${item?.staffIdentifier}`, {
                                                state: {
                                                    staffIdentifier: item?.staffIdentifier,
                                                    staffName: item?.staffName,
                                                },
                                            })
                                        }
                                    >
                                        <div className={styles.cardTopRow}>
                                            <span className={styles.staffAvatar}><User size={14} /></span>
                                            <div className={styles.staffInfo}>
                                                <strong>{item?.staffName}</strong>
                                                <span>{item?.staffCode || item?.staffIdentifier}</span>
                                            </div>
                                            <span className={`${styles.statusBadge} ${statusMeta.className}`}>{statusMeta.label}</span>
                                        </div>

                                        {Array.isArray(item?.issues) && item.issues.length > 0 ? (
                                            <p className={styles.issueText}>{item.issues[0]}</p>
                                        ) : (
                                            <p className={styles.issueText}>All key payroll checks are healthy.</p>
                                        )}

                                        <span className={styles.cardCta}>
                                            Generate FnF <ArrowRight size={13} />
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    ) : null}
                </section>
            )}

            <section className={styles.footerHint}>
                <CheckCircle2 size={14} />
                <span>Tip: Start with BLOCKED staff first to clear operational risk before payroll run.</span>
            </section>
        </div>
    )
}

export default FnFHomeScreen